<template>
  <div class="ad-page">
    <div class="ad-toolbar">
      <select v-model="filters.superAdminId" class="ad-select" aria-label="Superadmin" @change="reload">
        <option value="">Todos los superadmins</option>
        <option v-for="a in options.superAdmins" :key="a.id" :value="a.id">{{ a.name }}</option>
      </select>
      <select v-model="filters.parkId" class="ad-select" aria-label="Parque" @change="reload">
        <option value="">Todos los parques</option>
        <option v-for="p in options.parks" :key="p.id" :value="p.id">{{ p.name }}</option>
      </select>
      <select v-model="filters.action" class="ad-select" aria-label="Tipo de acción" @change="reload">
        <option value="">Todas las acciones</option>
        <option v-for="a in options.actions" :key="a.value" :value="a.value">{{ a.label }}</option>
      </select>
      <input v-model="filters.from" type="date" class="ad-input" aria-label="Desde" @change="reload" />
      <input v-model="filters.to" type="date" class="ad-input" aria-label="Hasta" @change="reload" />
      <span class="ad-toolbar__spacer"></span>
      <a class="ad-btn ad-btn--ghost" :href="csvUrl" target="_blank" rel="noopener">
        <span class="mdi mdi-download"></span> Exportar CSV
      </a>
    </div>

    <div class="ad-card">
      <p v-if="loading && !items.length" class="ad-empty">Cargando bitácora…</p>
      <p v-else-if="loadError" class="ad-empty">{{ loadError }}</p>
      <p v-else-if="!items.length" class="ad-empty">No hay eventos con estos filtros.</p>
      <div v-else class="ad-table-wrap">
        <table class="ad-table">
          <thead>
            <tr><th>Fecha y hora</th><th>Superadmin</th><th>Acción</th><th>Parque</th><th class="ad-col-actions"></th></tr>
          </thead>
          <tbody>
            <template v-for="row in items" :key="row.id">
              <tr class="row" :class="{ 'row--open': open === row.id }" @click="toggle(row)">
                <td class="ad-mono">{{ formatDateTime(row.createdAt) }}</td>
                <td>
                  <span class="who">
                    <span class="who__avatar">{{ initials(row.superAdminName) }}</span>
                    <span class="ad-cell-name">{{ row.superAdminName }}</span>
                  </span>
                </td>
                <td>
                  <div style="color:#1e3a5f">{{ row.summary }}</div>
                  <div class="ad-cell-sub">{{ row.actionLabel }}</div>
                </td>
                <td><span v-if="row.parkName" class="ad-tag">{{ row.parkName }}</span><template v-else>—</template></td>
                <td class="ad-col-actions"><span class="mdi" :class="open === row.id ? 'mdi-chevron-up' : 'mdi-chevron-down'" style="font-size:20px"></span></td>
              </tr>
              <tr v-if="open === row.id" class="detail">
                <td colspan="5">
                  <div v-if="row.session" class="session">
                    <p class="session__head">
                      Sesión de impersonación como <strong>{{ row.session.targetUserEmail }}</strong> ·
                      <span class="ad-pill" :class="SESSION_PILL[row.session.status].cls">{{ SESSION_PILL[row.session.status].label }}</span>
                      · {{ row.session.actionCount }} {{ row.session.actionCount === 1 ? 'acción' : 'acciones' }}
                    </p>
                    <p v-if="sessionLoading" class="ad-hint">Cargando sesión…</p>
                    <ol v-else-if="sessionDetail" class="timeline">
                      <li v-for="e in sessionDetail.events" :key="e.id" class="timeline__item" :class="`timeline__item--${e.action}`">
                        <span class="ad-mono timeline__time">{{ formatTime(e.createdAt) }}</span>
                        <span class="timeline__text">
                          <strong v-if="e.action !== 'IMPERSONATED_ACTION'">{{ e.actionLabel }}</strong>
                          <template v-else>{{ e.summary }}<span v-if="e.params?.status" class="ad-hint"> · {{ e.params.status }}</span></template>
                        </span>
                      </li>
                      <li v-if="sessionDetail.session.status !== 'ENDED'" class="timeline__item">
                        <span class="ad-mono timeline__time">—</span>
                        <span class="timeline__text ad-hint">{{ sessionDetail.session.status === 'ACTIVE' ? 'La sesión sigue abierta.' : 'La sesión venció sin cierre manual.' }}</span>
                      </li>
                    </ol>
                    <p v-if="sessionError" class="ad-error">{{ sessionError }}</p>
                  </div>
                  <pre class="params">{{ pretty(row.params) }}</pre>
                </td>
              </tr>
            </template>
          </tbody>
        </table>
      </div>
      <div v-if="hasMore" style="padding: 12px; text-align: center">
        <button type="button" class="ad-btn ad-btn--ghost" :disabled="loading" @click="loadMore">{{ loading ? 'Cargando…' : 'Cargar más' }}</button>
      </div>
    </div>
  </div>
</template>

<script setup>
import { ref, reactive, computed, onMounted } from 'vue'
import { useRoute } from 'vue-router'
import { listAudit, getAuditFilters, getImpersonationDetail, auditCsvUrl } from '@/services/superadmin'
import { formatDateTime, formatTime, initials, errorText } from '@/components/Superadmin/adminFormat'

const route = useRoute()
const SESSION_PILL = {
  ACTIVE: { label: 'Abierta', cls: 'ad-pill--red' },
  ENDED: { label: 'Terminada', cls: 'ad-pill--gray' },
  EXPIRED: { label: 'Vencida', cls: 'ad-pill--amber' },
}

const filters = reactive({
  superAdminId: '',
  parkId: typeof route.query.parkId === 'string' ? route.query.parkId : '',
  action: '',
  from: '',
  to: '',
})
const options = reactive({ superAdmins: [], parks: [], actions: [] })
const items = ref([])
const page = ref(1)
const hasMore = ref(false)
const loading = ref(true)
const loadError = ref('')
const open = ref(null)
const sessionDetail = ref(null)
const sessionLoading = ref(false)
const sessionError = ref('')

const csvUrl = computed(() => auditCsvUrl({ ...filters }))

let seq = 0
async function load(reset) {
  const mine = ++seq
  loading.value = true
  loadError.value = ''
  if (reset) page.value = 1
  try {
    const res = await listAudit({ ...filters, page: page.value })
    if (mine !== seq) return
    items.value = reset ? res.items : [...items.value, ...res.items]
    hasMore.value = res.hasMore
  } catch (err) {
    if (mine === seq) loadError.value = errorText(err, 'No se pudo cargar la bitácora.')
  } finally {
    if (mine === seq) loading.value = false
  }
}

function reload() { open.value = null; load(true) }
function loadMore() { page.value += 1; load(false) }

async function toggle(row) {
  if (open.value === row.id) { open.value = null; return }
  open.value = row.id
  sessionDetail.value = null
  sessionError.value = ''
  if (!row.session) return
  sessionLoading.value = true
  try {
    const detail = await getImpersonationDetail(row.session.id)
    if (open.value === row.id) sessionDetail.value = detail
  } catch (err) {
    sessionError.value = errorText(err, 'No se pudo cargar la sesión.')
  } finally {
    sessionLoading.value = false
  }
}

const pretty = (p) => (p == null ? 'Sin parámetros' : JSON.stringify(p, null, 2))

onMounted(async () => {
  load(true)
  try {
    Object.assign(options, await getAuditFilters())
  } catch { /* los filtros por lista son opcionales: la tabla funciona sin ellos */ }
})
</script>

<style scoped>
.row { cursor: pointer; }
.row--open td { background: #f8fafc; }
.who { display: inline-flex; align-items: center; gap: 8px; }
.who__avatar {
  width: 24px; height: 24px; border-radius: 50%; background: #e84545; color: #fff; display: inline-flex;
  align-items: center; justify-content: center; font: 600 10px Inter, sans-serif;
}
.detail td { background: #f8fafc; padding: 14px 18px 18px; }
.params {
  margin: 0; padding: 12px; background: #0f172a; color: #cbd5e1; border-radius: 8px; overflow: auto; max-height: 260px;
  font: 400 11px/1.5 var(--font-mono); white-space: pre-wrap; word-break: break-word;
}
.session { margin-bottom: 12px; }
.session__head { margin: 0 0 8px; font: 400 12px Inter, sans-serif; color: #475569; }
.timeline { list-style: none; margin: 0; padding: 0 0 0 12px; border-left: 2px solid #e2e8f0; display: flex; flex-direction: column; gap: 6px; }
.timeline__item { display: flex; gap: 12px; align-items: baseline; font: 400 12px Inter, sans-serif; color: #334155; }
.timeline__item--IMPERSONATION_STARTED, .timeline__item--IMPERSONATION_ENDED { color: #b91c1c; }
.timeline__time { flex-shrink: 0; width: 64px; color: #64748b; }
.timeline__text { min-width: 0; word-break: break-word; }
</style>